import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cafInboxApi, CafAttachment, CafFolder, CafMessageDetail, CafMessageSummary, CafOriginal } from '../api/cafInbox';
import CafMessageBody from '../components/CafMessageBody';
import { useAuth } from '../context/AuthContext';
import './CafInbox.css';

const LIMIT = 50;
const formatDate = (value: string | null) => value ? new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/London',
}).format(new Date(value)) : 'Unknown';
const safeFileStem = (subject: string) =>
  (subject || 'message').replace(/[^a-z0-9 _-]/gi, '').trim().slice(0, 80) || 'message';
const friendlyListError = (code: string) => code === 'search_options_rejected' ? 'Search options were not accepted. Check your search and date range. If they are valid, the CAF backend may need updating.' : code === 'search_timeout' ? 'Search took too long. Please try again or refine your search.' : code === 'unavailable' || code === 'request_failed'
  ? 'CAF inbox is unavailable. Please try again later.' : 'CAF messages could not be loaded.';

export default function CafInbox() {
  const { isAdmin } = useAuth();
  const [query, setQuery] = useState('');
  const [includeBody, setIncludeBody] = useState(false);
  const [source, setSource] = useState('');
  const [folder, setFolder] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [folders, setFolders] = useState<CafFolder[]>([]);
  const [graphEnabled, setGraphEnabled] = useState(false);
  const [folderError, setFolderError] = useState(false);
  const [items, setItems] = useState<CafMessageSummary[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [resultPage, setResultPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<CafMessageSummary | null>(null);
  const [readerOpen, setReaderOpen] = useState(false);
  const [detail, setDetail] = useState<CafMessageDetail | null>(null);
  const [detailError, setDetailError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [downloading, setDownloading] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [importStatus, setImportStatus] = useState('');
  const [importing, setImporting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const importTrigger = useRef<HTMLButtonElement>(null);
  const filtersTrigger = useRef<HTMLButtonElement>(null);
  const importInput = useRef<HTMLInputElement>(null);
  const importDialog = useRef<HTMLDivElement>(null);
  const previousQuery = useRef('');
  const detailRequest = useRef(0);
  const downloadRequest = useRef(0);

  useEffect(() => { if (importOpen) importInput.current?.focus(); }, [importOpen]);
  const closeImport = () => { setImportOpen(false); importTrigger.current?.focus(); };
  const handleDialogKey = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') { event.preventDefault(); closeImport(); }
    if (event.key === 'Tab') {
      const controls = importDialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)');
      if (!controls?.length) return;
      const first = controls[0]; const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  };
  const clearFilters = () => { setQuery(''); setIncludeBody(false); setSource(''); setFolder(''); setFrom(''); setTo(''); setPage(1); };
  const activeFilters = [
    { name: 'Search', value: query, clear: () => setQuery('') },
    { name: 'Contents', value: includeBody ? 'Included' : '', clear: () => setIncludeBody(false) },
    { name: 'Source', value: source === 'unknown' ? 'Not recorded' : source === 'purview' ? 'Mailbox archive' : source === 'graph' ? 'Microsoft 365' : '', clear: () => setSource('') },
    { name: 'Folder', value: folder, clear: () => setFolder('') },
    { name: 'Received on or after', value: from, clear: () => setFrom('') },
    { name: 'Received on or before', value: to, clear: () => setTo('') },
  ].filter(item => item.value);
  const folderNames = Array.from(new Set(folders.map(item => item.folder).filter((name): name is string => Boolean(name))));
  const folderLabel = (name: string) => {
    const leaf = name.split('/').filter(Boolean).pop() || name;
    const duplicates = folderNames.filter(other => (other.split('/').filter(Boolean).pop() || other) === leaf);
    const separator = name.lastIndexOf('/');
    const parent = separator >= 0 ? name.slice(0, separator) : '';
    return duplicates.length > 1 ? `${leaf} — ${parent || 'Root'}` : leaf;
  };

  useEffect(() => {
    let active = true;
    setFolderError(false);
    cafInboxApi.getFolders().then(result => {
      if (active) {
        setFolders(result.items);
        setGraphEnabled(result.graph_enabled === true || result.items.some(item => item.source === 'graph'));
      }
    }).catch(() => {
      if (active) { setFolders([]); setFolderError(true); }
    });
    return () => { active = false; };
  }, [reload]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const delay = previousQuery.current !== query ? 300 : 0;
    previousQuery.current = query;
    setLoading(true);
    setListError('');
    const search = () => cafInboxApi.searchMessages({
      q: query, source, folder, date_from: from, date_to: to, page, limit: LIMIT, include_body: includeBody,
    }, controller.signal).then(result => {
      if (active) { setItems(result.items); setHasMore(result.has_more); setResultPage(result.page); }
    }).catch(error => {
      if (active) {
        setListError(friendlyListError(error instanceof Error ? error.message : 'request_failed'));
      }
    }).finally(() => {
      if (active) setLoading(false);
    });
    const timer = delay ? window.setTimeout(search, delay) : undefined;
    if (!delay) search();
    return () => { active = false; controller.abort(); window.clearTimeout(timer); };
  }, [query, includeBody, source, folder, from, to, page, reload]);

  const updateFilter = (setter: (value: string) => void) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setter(event.target.value);
      setPage(1);
    };

  const openMessage = async (item: CafMessageSummary) => {
    const request = ++detailRequest.current;
    ++downloadRequest.current;
    setSelected(item);
    setReaderOpen(true);
    setDetail(null);
    setDetailError('');
    setDownloadError('');
    setDownloading('');
    setDetailLoading(true);
    try {
      const result = await cafInboxApi.getMessage(item.id);
      if (request === detailRequest.current) setDetail(result);
    } catch {
      if (request === detailRequest.current) setDetailError('This message could not be loaded.');
    } finally {
      if (request === detailRequest.current) setDetailLoading(false);
    }
  };

  const closeMessage = () => {
    setReaderOpen(false);
  };

  const downloadOriginal = async (message: CafMessageDetail, original: CafOriginal) => {
    const request = ++downloadRequest.current;
    setDownloading(`original:${original.message_id}`);
    setDownloadError('');
    try {
      await cafInboxApi.downloadOriginal(original.message_id, `${safeFileStem(message.subject)}.${original.format}`);
    } catch {
      if (request === downloadRequest.current) setDownloadError(`The original ${original.format.toUpperCase()} could not be downloaded. Please try again.`);
    } finally {
      if (request === downloadRequest.current) setDownloading('');
    }
  };

  const downloadAttachment = async (message: CafMessageDetail, attachment: CafAttachment) => {
    const request = ++downloadRequest.current;
    setDownloading(attachment.id);
    setDownloadError('');
    try {
      await cafInboxApi.downloadAttachment(message.id, attachment.id, attachment.filename);
    } catch {
      if (request === downloadRequest.current) setDownloadError('The attachment could not be downloaded. Please try again.');
    } finally {
      if (request === downloadRequest.current) setDownloading('');
    }
  };

  const importSelectedMessage = async () => {
    if (!file) return;
    setImporting(true);
    setImportStatus('');
    try {
      const result = await cafInboxApi.importMessage(file);
      setImportStatus(result.duplicate
        ? `Duplicate message${result.identity_conflict ? ' — identity conflict recorded' : ''}.`
        : `Message imported${result.identity_conflict ? ' — identity conflict recorded' : ''}.`);
      setPage(1);
      setReload(value => value + 1);
    } catch (error) {
      const code = error instanceof Error ? error.message : '';
      if (['invalid_import', 'too_large', 'invalid_request'].includes(code)) {
        setImportStatus('The MSG file is invalid or exceeds an import limit.');
      } else if (code === 'forbidden') {
        setImportStatus('You are not permitted to import messages.');
      } else {
        setImportStatus('The message could not be imported.');
      }
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className={`caf-inbox ${readerOpen ? 'caf-has-selection' : ''}`}>
      <div className="caf-heading-region">
      <header className="caf-title">
        <div className="caf-brand"><MailIcon /><div><h1>CAF Inbox</h1><p>Stored mail · Read only</p></div></div>
        <div className="caf-search-controls">
          <label className="caf-search"><span className="caf-sr-only">Search all emails</span>
            <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4 4" /></svg>
            <input type="search" placeholder="Search all emails" title={includeBody ? 'Search sender, recipients, subject and email contents across all folders' : 'Search sender, recipients and subject across all folders'} aria-describedby="caf-search-hint" value={query} onChange={updateFilter(setQuery)} />
          </label>
          <div className="caf-search-options">
            <label><input type="checkbox" checked={includeBody} aria-describedby="caf-contents-hint" onChange={event => { setIncludeBody(event.target.checked); setPage(1); }} />Include email contents</label>
            <span id="caf-contents-hint">Searching email contents can be slower.</span>
          </div>
          <span id="caf-search-hint" className="caf-sr-only">{includeBody ? 'Searches sender, recipients, subject and email contents.' : 'Searches sender, recipients and subject. Select Include email contents to search message text too.'}</span>
        </div>
        <div className="caf-toolbar-actions">
          <button type="button" onClick={() => setReload(value => value + 1)} aria-label="Refresh messages" title="Refresh messages">↻ <span>Refresh</span></button>
          <button type="button" ref={filtersTrigger} aria-expanded={filtersOpen} aria-controls="caf-advanced-filters" onClick={() => setFiltersOpen(value => !value)}>Filters{[folder, source, from, to].filter(Boolean).length > 0 && <span className="caf-filter-count">{[folder, source, from, to].filter(Boolean).length}</span>}</button>
          {isAdmin && <button type="button" ref={importTrigger} onClick={() => setImportOpen(true)}>Import MSG</button>}
        </div>
      </header>
      {filtersOpen && <div className="caf-advanced-filters" id="caf-advanced-filters" aria-label="CAF filters">
          <label>Folder<select value={folder} onChange={updateFilter(setFolder)}><option value="">All folders</option>{folderNames.map(name => <option key={name} value={name}>{folderLabel(name)}</option>)}</select></label>
          <label>Source
            <select value={source} onChange={updateFilter(setSource)}>
              <option value="">All sources</option><option value="purview">Mailbox archive</option>
              {graphEnabled && <option value="graph">Microsoft 365</option>}
              <option value="unknown">Not recorded</option>
            </select>
          </label>
          <label>Received on or after<input type="date" value={from} onChange={updateFilter(setFrom)} /></label>
          <label>Received on or before<input type="date" value={to} onChange={updateFilter(setTo)} /></label>
          <p>Filter by received date</p>
          <button type="button" className="caf-filters-done" onClick={() => { setFiltersOpen(false); filtersTrigger.current?.focus(); }}>Done with filters</button>
          {folderError && <div className="caf-filter-error" role="alert"><p>Folder filters are unavailable. Messages remain visible without a folder filter.</p><button type="button" onClick={() => setReload(value => value + 1)}>Retry folders</button></div>}
      </div>}
      </div>
      {activeFilters.length > 0 && <div className="caf-active-filters" aria-label="Active filters">
        {activeFilters.map(item => <button key={item.name} title={item.value} aria-label={`Remove ${item.name.toLowerCase()} filter: ${item.value}`} onClick={() => { item.clear(); setPage(1); }}><span>{item.name === 'Folder' ? `Searching: ${item.value.split('/').pop()}` : `${item.name}: ${item.value}`}</span><span aria-hidden="true">×</span></button>)}
        <button className="caf-clear" onClick={clearFilters}>Clear all filters</button>
      </div>}
      <div className="caf-columns">
        <section className="caf-list" aria-label="CAF messages">
          <header className="caf-list-heading"><h2>{folder ? folder.split('/').pop() : 'All emails'}</h2><span>{loading ? 'Searching…' : listError ? 'Not updated' : 'Search results'}</span></header>
          {items.length > 0 && (loading || listError) && <p className="caf-results-notice" role="status">{loading ? 'Previous results — searching…' : 'Previous results — not updated for this search.'}</p>}
          {listError && <div className="caf-list-error" role="alert"><p>{listError}</p><button onClick={() => setReload(value => value + 1)}>Retry</button></div>}
          <div className="caf-list-scroll" aria-busy={loading}>
          {loading && items.length === 0 ? <div className="caf-list-state" role="status"><span className="caf-loading-dot" />Loading CAF messages…</div> : !loading && !listError && items.length === 0 ? <div className="caf-list-state"><MailIcon /><h3>No messages match these filters.</h3><p>Try a different search or broaden your filters.</p>{activeFilters.length > 0 && <button onClick={clearFilters}>Clear all filters</button>}</div> : (
            <>
              {items.map(item => (
                <button type="button" key={item.id} className={`caf-message-row ${selected?.id === item.id ? 'selected' : ''}`}
                  aria-pressed={selected?.id === item.id} onClick={() => openMessage(item)}>
                  <span className="caf-message-top"><span className="caf-sender">{item.sender || 'Unknown sender'}</span><time dateTime={item.received_at || undefined} title={formatDate(item.received_at)}>{item.received_at ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' }).format(new Date(item.received_at)) : 'Unknown date'}</time></span>
                  <strong>{item.subject || '(No subject)'}</strong>
                  <span className="caf-message-bottom"><small>To: {item.recipients.join(', ') || 'Unknown recipient'}</small>{item.has_attachments && <span className="caf-paperclip"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="m8 13 7-7a3 3 0 0 1 4 4l-9 9a5 5 0 0 1-7-7l10-10M6 15l9-9" /></svg><span className="caf-sr-only">Has attachments</span></span>}</span>
                </button>
              ))}
            </>
          )}
          </div>
              <nav className="caf-pages" aria-label="Message pages">
                <button aria-label="Previous page" disabled={loading || Boolean(listError) || page <= 1}
                  onClick={() => setPage(value => value - 1)}>Previous</button>
                <span>{loading || listError
                  ? (items.length ? 'Previous results' : loading ? 'Searching…' : '')
                  : items.length ? `${(resultPage - 1) * LIMIT + 1}–${(resultPage - 1) * LIMIT + items.length}` : '0 results'}</span>
                <button aria-label="Next page" disabled={loading || Boolean(listError) || !hasMore}
                  onClick={() => setPage(value => value + 1)}>Next</button>
              </nav>
        </section>
        <article className="caf-reader">
          {!selected ? <div className="caf-reader-empty"><span className="caf-empty-icon"><MailIcon /></span><h2>{listError ? 'Mail is unavailable' : 'Select a message'}</h2>
            {listError ? <p>We could not retrieve your stored mail.<br />Use Refresh above to try again.</p> : <p>Choose an email to view its contents,<br />attachments and source details.</p>}<span className="caf-read-only">Read-only archive</span></div> : (
            <><button className="caf-back" onClick={closeMessage}>← Back to messages</button>
              {loading || listError ? <p className="caf-results-notice">{loading ? 'Search is updating. Your open message is kept below.' : 'Search could not be updated. Your open message is kept below.'}</p> : !items.some(item => item.id === selected.id) && <p className="caf-results-notice" role="note">This open message is not in the current results.</p>}
              {detailLoading ? <p>Loading message…</p> : detailError ? (
                <div role="alert"><p>{detailError}</p>
                  <button onClick={() => openMessage(selected)}>Retry message</button></div>
              ) : detail && <MessageDetail message={detail} downloadError={downloadError}
                downloading={downloading} onOriginal={downloadOriginal} onAttachment={downloadAttachment} />}
            </>
          )}
        </article>
      </div>
      {isAdmin && importOpen && createPortal(<div className="caf-inbox caf-dialog-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeImport(); }}>
        <div className="caf-import-dialog" role="dialog" aria-modal="true" aria-labelledby="caf-import-title" ref={importDialog} onKeyDown={handleDialogKey}>
          <h2 id="caf-import-title">Import a stored message</h2><p>Add one Outlook MSG file to the CAF archive.</p>
          <label>Import MSG<input ref={importInput} type="file" accept=".msg,application/vnd.ms-outlook" onChange={event => { setFile(event.target.files?.[0] || null); setImportStatus(''); }} /></label>
          {file && <p className="caf-import-filename">Selected: {file.name}</p>}
          {importStatus && <p role="status">{importStatus}</p>}
          <div className="caf-dialog-actions"><button disabled={!file || importing} onClick={importSelectedMessage}>{importing ? 'Importing…' : 'Import selected MSG'}</button><button onClick={closeImport}>Close</button></div>
        </div>
      </div>, document.body)}
    </section>
  );
}

function MailIcon() { return <svg aria-hidden="true" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 6 9 7 9-7" /></svg>; }

function MessageDetail({ message, downloadError, downloading, onOriginal, onAttachment }: {
  message: CafMessageDetail; downloadError: string; downloading: string;
  onOriginal: (message: CafMessageDetail, original: CafOriginal) => void;
  onAttachment: (message: CafMessageDetail, attachment: CafAttachment) => void;
}) {
  const originals: CafOriginal[] = message.originals || [{ source: message.source, message_id: message.id, format: message.source === 'graph' ? 'eml' : 'msg' }];
  const sourceName = (source: string) => source === 'purview' ? 'Purview' : source === 'graph' ? 'Microsoft 365' : 'Unknown provenance';
  return <><header className="caf-detail-header"><div className="caf-detail-eyebrow">Stored message <span>Read only</span></div><h2>{message.subject || '(No subject)'}</h2>
    <div className="caf-detail-sender"><span className="caf-avatar" aria-hidden="true">{(message.sender || '?').charAt(0).toUpperCase()}</span><div><strong>{message.sender || 'Unknown sender'}</strong><time>{formatDate(message.received_at)}</time></div></div>
    <details className="caf-message-metadata"><summary>Recipients &amp; source details</summary><dl>
    <dt>Recipients</dt><dd>{message.recipients.length ? message.recipients.join(', ') : 'Unknown'}</dd>
    <dt>Sent</dt><dd>{formatDate(message.sent_at)}</dd>
    <dt>Received</dt><dd>{formatDate(message.received_at)}</dd>
    <dt>Last known source</dt><dd>{sourceName(message.source)}{message.folder ? ` — ${message.folder}` : ''}</dd>
  </dl>{message.sources.length > 1 && <ul className="caf-source-records">{message.sources.map((record, index) => <li key={index}>{sourceName(record.source)} · {record.folder || 'Unknown folder'}{record.location_subtype ? ` · ${record.location_subtype}` : ''}</li>)}</ul>}</details>
    {message.identity_conflict && <p className="caf-warning" role="note"><strong>Identity conflict</strong> · Identity conflict recorded for this stored copy.</p>}
    {originals.map(original => <button key={original.message_id} className="caf-original" disabled={Boolean(downloading)} onClick={() => onOriginal(message, original)}>
      {downloading === `original:${original.message_id}` ? 'Downloading original…' : `Download original ${original.format.toUpperCase()}`}
      {originals.length > 1 ? ` · ${sourceName(original.source)}` : ''}
    </button>)}
    {downloadError && <p className="caf-download-error" role="alert">{downloadError}</p>}
    {message.attachments.length > 0 && <section className="caf-attachments"><h3>Attachments <span>{message.attachments.length}</span></h3><ul>
      {message.attachments.map(attachment => <li key={attachment.id}><button disabled={Boolean(downloading)} onClick={() => onAttachment(message, attachment)}>
        <span className="caf-file-icon" aria-hidden="true">↓</span><span className="caf-sr-only">{downloading === attachment.id ? 'Downloading' : 'Download'}</span><span><strong>{attachment.filename}</strong><small>{downloading === attachment.id ? 'Downloading…' : `${attachment.content_type || 'Unknown type'} · ${attachment.size.toLocaleString()} bytes`}</small></span>
      </button></li>)}
    </ul></section>}</header>
    <CafMessageBody html={message.body_html} text={message.body_text} /></>;
}
